import * as React from "react";
import { InstallAdminButton } from "@shared/ui/common/AdminPwa";
import { useAuth } from "@app/providers/auth";
import { useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { formatFullName } from "@shared/lib/utils/formatName";
import { ChevronDown } from "lucide-react";
import { useSignOut } from "@shared/hooks/useSignOut";
import { AUTH_KEY } from "@shared/lib/tokenStorage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Button,
} from "@ui/core";

function useFocusTrap(active: boolean, onEscape?: () => void) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!active) return;
    const el = ref.current;
    if (!el) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (onEscape) onEscape();
        else (document.activeElement as HTMLElement | null)?.blur?.();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = el!.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
      );
      const list = Array.from(focusables).filter(
        (f) => f.offsetParent !== null,
      );
      const first = list[0];
      const last = list[list.length - 1];
      if (!first || !last) return;
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (activeEl === first || !el!.contains(activeEl))) {
        e.preventDefault();
        last.focus();
      } else if (
        !e.shiftKey &&
        (activeEl === last || !el!.contains(activeEl))
      ) {
        e.preventDefault();
        first.focus();
      }
    }
    el.addEventListener("keydown", onKeyDown as any);
    return () => el.removeEventListener("keydown", onKeyDown as any);
  }, [active, onEscape]);
  return ref;
}

export function AccountMenu({ storeName }: { storeName?: string | null }) {
  const { t: tUi } = useTranslation("ui");
  const { user, expiresAt } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = useFocusTrap(open, () => setOpen(false));
  const signOut = useSignOut();
  const { location } = useRouterState({
    select: (s) => ({ location: s.location }),
  });
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const wasOpenRef = React.useRef(false);

  const displayName = React.useMemo(() => {
    if (user?.firstName || user?.lastName) {
      return formatFullName(user.firstName, user.lastName);
    }
    return (
      user?.name || user?.email?.split("@")[0] || t("accountMenu.userFallback")
    );
  }, [user, t]);

  const initials = React.useMemo(() => {
    if (user?.firstName && user?.lastName) {
      return (user.firstName[0] + (user.lastName[0] || "")).toUpperCase();
    }
    const nameStr = displayName.replace(/\W/g, "");
    return nameStr.slice(0, 2).toUpperCase();
  }, [user, displayName]);

  const email = user?.email ?? "user@example.com";

  const [rememberedSession, setRememberedSession] = React.useState(false);

  React.useEffect(() => {
    if (!open || typeof window === "undefined") return;
    try {
      setRememberedSession(!!window.localStorage.getItem(AUTH_KEY));
    } catch {
      setRememberedSession(false);
    }
  }, [open]);

  const expiryText = React.useMemo(() => {
    if (!expiresAt)
      return t("accountMenu.session.expiryAutomatic", {
        defaultValue:
          "Access refreshes automatically while your session is valid.",
      });
    const date = new Date(expiresAt);
    if (Number.isNaN(date.getTime()))
      return t("accountMenu.session.expiryAutomatic", {
        defaultValue:
          "Access refreshes automatically while your session is valid.",
      });
    return t("accountMenu.session.expiresAt", {
      defaultValue:
        "Access token expires at {{time}} and refreshes automatically when possible.",
      time: new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(date),
    });
  }, [expiresAt, t]);

  function onLogout() {
    setOpen(false);
    setIsLogoutConfirmOpen(true);
  }

  async function handleFinalLogout() {
    setIsLoggingOut(true);
    try {
      await signOut();
    } finally {
      setIsLoggingOut(false);
      setIsLogoutConfirmOpen(false);
    }
  }

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    function onDocPointerDown(e: MouseEvent | PointerEvent) {
      const root = containerRef.current;
      if (!root) return;
      const t = e.target as Node | null;
      if (t && !root.contains(t)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onDocPointerDown as any, true);
    document.addEventListener("keydown", onKey as any);
    return () => {
      document.removeEventListener(
        "pointerdown",
        onDocPointerDown as any,
        true,
      );
      document.removeEventListener("keydown", onKey as any);
    };
  }, [open]);

  // Close on route change
  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Initial focus when opening, restore to trigger on close
  React.useEffect(() => {
    const panel = ref.current;
    if (open) {
      wasOpenRef.current = true;
      const focusTarget = panel?.querySelector<HTMLElement>(
        '[data-autofocus], a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
      );
      // Defer to next tick to ensure panel is in DOM
      const t = window.setTimeout(() => focusTarget?.focus(), 0);
      return () => window.clearTimeout(t);
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus?.();
    }
  }, [open, ref]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={tUi("openAccountMenu", {
          defaultValue: "Open account menu",
        })}
        onClick={() => setOpen((v) => !v)}
        ref={triggerRef}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border p-1 hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/50 sm:h-auto sm:w-auto sm:gap-2 sm:px-2 sm:py-1.5"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold">
          {initials}
        </span>
        <span className="hidden max-w-[10rem] truncate text-sm font-medium xl:block">
          {displayName}
        </span>
        <ChevronDown
          size={14}
          className="hidden text-muted-foreground sm:block"
        />
      </button>
      {open ? (
        <div
          ref={ref}
          role="menu"
          tabIndex={-1}
          className="absolute right-0 mt-2 w-56 rounded-md border bg-popover text-popover-foreground p-2 shadow-lg"
        >
          <div className="mb-2 rounded-md bg-muted/40 p-2 text-xs">
            <div className="font-medium">{displayName}</div>
            {email && email !== displayName && (
              <div className="text-muted-foreground">{email}</div>
            )}
            {storeName ? (
              <div className="mt-2 border-t border-border/70 pt-2">
                <div className="font-medium text-foreground">
                  {t("accountMenu.store", { defaultValue: "Store" })}
                </div>
                <div className="text-muted-foreground">{storeName}</div>
              </div>
            ) : null}
          </div>
          <InstallAdminButton menuItem />
          <a
            href="/account/profile"
            role="menuitem"
            className="block rounded-md px-2 py-1.5 hover:bg-primary/10"
            data-autofocus
          >
            {t("accountMenu.profile")}
          </a>
          <div className="my-2 rounded-md border border-border/70 bg-muted/30 p-2 text-xs text-muted-foreground">
            <div className="font-medium text-foreground">
              {t("accountMenu.session.title", { defaultValue: "Session" })}
            </div>
            <div className="mt-1">
              {rememberedSession
                ? t("accountMenu.session.remembered", {
                    defaultValue: "Remembered on this device.",
                  })
                : t("accountMenu.session.currentSession", {
                    defaultValue: "Current browser session only.",
                  })}
            </div>
            <div className="mt-1">{expiryText}</div>
            <div className="mt-1">
              {t("accountMenu.session.tabs", {
                defaultValue:
                  "Signing out here signs out other open admin tabs.",
              })}
            </div>
          </div>
          <div className="my-1 h-px bg-border" role="separator" />
          <button
            role="menuitem"
            className="mt-1 w-full rounded-md px-2 py-1.5 text-left text-destructive hover:bg-destructive/10"
            onClick={onLogout}
          >
            {t("accountMenu.logout", { defaultValue: "Log out" })}
          </button>
        </div>
      ) : null}
      <Dialog open={isLogoutConfirmOpen} onOpenChange={setIsLogoutConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("accountMenu.logoutConfirm.title", {
                defaultValue: "Log out of Storizone?",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("accountMenu.logoutConfirm.description", {
                defaultValue:
                  "You will be signed out of all open admin tabs. Any unsaved changes in other tabs might be lost.",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setIsLogoutConfirmOpen(false)}
              disabled={isLoggingOut}
            >
              {t("common:cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              variant="destructive"
              onClick={handleFinalLogout}
              loading={isLoggingOut}
            >
              {t("accountMenu.logout", { defaultValue: "Log out" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
